import React, { useState } from 'react';
import { X, Save, Mail, Phone, MapPin, Briefcase, GraduationCap, DollarSign, Calendar, FileText, Shield, Edit, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  'New',
  'Contacted',
  'Screening Scheduled',
  'Qualified',
  'Submitted to Client',
  'Placed',
  'Not a Fit',
  'On Hold'
];

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'na', label: 'N/A' }
];

const YES_NO_MAYBE_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'na', label: 'N/A' }
];

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'employed', label: 'Currently Employed' },
  { value: 'unemployed', label: 'Unemployed / Seeking' },
  { value: 'transitioning', label: 'Military Transitioning' },
  { value: 'student', label: 'Student' },
  { value: 'na', label: 'N/A' }
];

const EDUCATION_OPTIONS = [
  { value: 'high-school', label: 'High School / GED' },
  { value: 'some-college', label: 'Some College' },
  { value: 'associates', label: "Associate's Degree" },
  { value: 'bachelors', label: "Bachelor's Degree" },
  { value: 'masters', label: "Master's Degree" },
  { value: 'doctorate', label: 'Doctorate' },
  { value: 'trade', label: 'Trade School / Technical Training' },
  { value: 'na', label: 'N/A' }
];

const BRANCH_OPTIONS = [
  { value: 'army', label: 'Army' },
  { value: 'navy', label: 'Navy' },
  { value: 'air-force', label: 'Air Force' },
  { value: 'marines', label: 'Marines' },
  { value: 'coast-guard', label: 'Coast Guard' },
  { value: 'space-force', label: 'Space Force' },
  { value: 'na', label: 'N/A' }
];

const ApplicantDetailModal = ({ applicant, token, onClose, onUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // ALL editable fields state
  const [formData, setFormData] = useState({
    // Status & Notes
    status: applicant.status || 'New',
    recruiterNotes: applicant.recruiterNotes || '',
    
    // Contact Information
    fullLegalName: applicant.fullLegalName || '',
    preferredName: applicant.preferredName || '',
    email: applicant.email || '',
    phoneNumber: applicant.phoneNumber || '',
    cityState: applicant.cityState || '',
    howDidYouHear: applicant.howDidYouHear || '',
    
    // Current Employment
    currentEmploymentStatus: applicant.currentEmploymentStatus || '',
    currentEmployer: applicant.currentEmployer || '',
    currentJobTitle: applicant.currentJobTitle || '',
    datesOfEmployment: applicant.datesOfEmployment || '',
    
    // Professional Background
    primaryResponsibilities: applicant.primaryResponsibilities || '',
    employeesSupervised: applicant.employeesSupervised || '',
    equipmentTechnologies: applicant.equipmentTechnologies || '',
    keyAccomplishments: applicant.keyAccomplishments || '',
    
    // Military Background
    branchOfService: applicant.branchOfService || '',
    yearsOfService: applicant.yearsOfService || '',
    rankAtSeparation: applicant.rankAtSeparation || '',
    militaryOccupation: applicant.militaryOccupation || '',
    militaryDuties: applicant.militaryDuties || '',
    
    // Education
    highestEducation: applicant.highestEducation || '',
    collegeUniversity: applicant.collegeUniversity || '',
    degreeMajor: applicant.degreeMajor || '',
    tradeSchool: applicant.tradeSchool || '',
    certifications: applicant.certifications || '',
    
    // Job Search Preferences
    desiredJobTitles: applicant.desiredJobTitles || '',
    preferredIndustries: applicant.preferredIndustries || '',
    preferredLocations: applicant.preferredLocations || '',
    openToRelocation: applicant.openToRelocation || '',
    willingToTravel: applicant.willingToTravel || '',
    
    // Compensation
    desiredSalaryRange: applicant.desiredSalaryRange || '',
    bonusExpectations: applicant.bonusExpectations || '',
    availabilityToStart: applicant.availabilityToStart || '',
    idealNextRole: applicant.idealNextRole || '',
    
    // Screening Questions
    authorizedToWork: applicant.authorizedToWork || '',
    requiresSponsorship: applicant.requiresSponsorship || '',
    willingBackgroundCheck: applicant.willingBackgroundCheck || '',
    willingDrugScreen: applicant.willingDrugScreen || '',
    currentlyInterviewing: applicant.currentlyInterviewing || '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Clean up 'na' values to empty strings before saving
      const cleanedData = { ...formData };
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === 'na') {
          cleanedData[key] = '';
        }
      });
      
      await axios.put(
        `${API}/admin/applicants/${applicant.applicantId}`,
        {
          ...cleanedData,
          lastContactDate: new Date().toISOString()
        },
        {
          params: { token }
        }
      );
      onUpdate();
      setIsEditMode(false);
    } catch (error) {
      alert('Failed to update applicant');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const InfoSection = ({ title, icon: Icon, children }) => (
    <div className="mb-6">
      <div className="flex items-center mb-3 pb-2 border-b-2 border-[#d4a574]">
        <Icon className="text-[#d4a574] mr-2" size={20} />
        <h3 className="text-lg font-bold text-[#0a1628]">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );

  const InfoRow = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-4">
      <dt className="font-semibold text-gray-700">{label}:</dt>
      <dd className="col-span-2 text-gray-900">{value || 'N/A'}</dd>
    </div>
  );

  const EditableRow = ({ label, field, options = null }) => {
    const rawValue = formData[field] || '';
    const displayValue = rawValue === 'na' ? '' : rawValue;
    
    if (!isEditMode) {
      // For display, show the label if it's a dropdown option
      if (options && rawValue) {
        const option = options.find(o => o.value === rawValue);
        return <InfoRow label={label} value={option ? option.label : displayValue} />;
      }
      return <InfoRow label={label} value={displayValue} />;
    }

    if (options) {
      const selectValue = rawValue || 'na';
      return (
        <div className="grid grid-cols-3 gap-4 items-center">
          <dt className="font-semibold text-gray-700">{label}:</dt>
          <dd className="col-span-2">
            <Select value={selectValue} onValueChange={(v) => handleInputChange(field, v)}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {options.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </dd>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-3 gap-4 items-center">
        <dt className="font-semibold text-gray-700">{label}:</dt>
        <dd className="col-span-2">
          <Input
            value={displayValue}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="h-8"
          />
        </dd>
      </div>
    );
  };

  const EditableTextArea = ({ label, field, rows = 3 }) => {
    const value = formData[field] || '';
    
    if (!isEditMode) {
      if (!value) return null;
      return (
        <div className="col-span-3">
          <p className="font-semibold text-gray-700 mb-1">{label}:</p>
          <p className="text-gray-900 whitespace-pre-wrap">{value}</p>
        </div>
      );
    }

    return (
      <div className="col-span-3">
        <p className="font-semibold text-gray-700 mb-1">{label}:</p>
        <Textarea
          value={value}
          onChange={(e) => handleInputChange(field, e.target.value)}
          rows={rows}
          className="mt-1"
        />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full my-8">
        {/* Header */}
        <div className="bg-[#0a1628] text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div>
            <h2 className="text-2xl font-bold">{formData.fullLegalName || applicant.fullLegalName}</h2>
            <p className="text-sm text-gray-300 mt-1">Applicant ID: {applicant.applicantId}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setIsEditMode(!isEditMode)}
              variant="outline"
              size="sm"
              className={`${isEditMode ? 'bg-[#d4a574] text-[#0a1628] border-[#d4a574]' : 'bg-transparent text-white border-white'} hover:bg-[#d4a574] hover:text-[#0a1628]`}
            >
              {isEditMode ? <><Eye size={16} className="mr-1" /> View Mode</> : <><Edit size={16} className="mr-1" /> Edit Mode</>}
            </Button>
            <button
              onClick={onClose}
              className="text-white hover:text-[#d4a574] transition-colors"
            >
              <X size={28} />
            </button>
          </div>
        </div>

        {/* Edit Mode Banner */}
        {isEditMode && (
          <div className="bg-[#d4a574] text-[#0a1628] px-6 py-2 text-center font-semibold">
            Edit Mode Active — All fields are now editable. Click "Save All Changes" when done.
          </div>
        )}

        {/* Content */}
        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Left Column - Main Info */}
            <div className="md:col-span-2 space-y-6">
              {/* Contact Information */}
              <InfoSection title="Contact Information" icon={Mail}>
                <EditableRow label="Full Name" field="fullLegalName" />
                <EditableRow label="Preferred Name" field="preferredName" />
                <EditableRow label="Email" field="email" />
                <EditableRow label="Phone" field="phoneNumber" />
                <EditableRow label="Location" field="cityState" />
                <EditableRow label="How They Heard" field="howDidYouHear" />
              </InfoSection>

              {/* Employment */}
              <InfoSection title="Current Employment" icon={Briefcase}>
                <EditableRow label="Status" field="currentEmploymentStatus" options={EMPLOYMENT_STATUS_OPTIONS} />
                <EditableRow label="Current Employer" field="currentEmployer" />
                <EditableRow label="Job Title" field="currentJobTitle" />
                <EditableRow label="Dates" field="datesOfEmployment" />
              </InfoSection>

              {/* Professional Background */}
              <InfoSection title="Professional Background" icon={FileText}>
                <EditableTextArea label="Responsibilities" field="primaryResponsibilities" rows={3} />
                <EditableRow label="Employees Supervised" field="employeesSupervised" />
                <EditableTextArea label="Equipment/Technologies" field="equipmentTechnologies" rows={2} />
                <EditableTextArea label="Key Accomplishments" field="keyAccomplishments" rows={3} />
              </InfoSection>

              {/* Military Background */}
              <InfoSection title="Military Background" icon={Shield}>
                <EditableRow label="Branch" field="branchOfService" options={BRANCH_OPTIONS} />
                <EditableRow label="Years of Service" field="yearsOfService" />
                <EditableRow label="Rank" field="rankAtSeparation" />
                <EditableRow label="MOS/Occupation" field="militaryOccupation" />
                <EditableTextArea label="Key Duties" field="militaryDuties" rows={2} />
              </InfoSection>

              {/* Education */}
              <InfoSection title="Education & Certifications" icon={GraduationCap}>
                <EditableRow label="Highest Education" field="highestEducation" options={EDUCATION_OPTIONS} />
                <EditableRow label="College/University" field="collegeUniversity" />
                <EditableRow label="Degree/Major" field="degreeMajor" />
                <EditableRow label="Trade School" field="tradeSchool" />
                <EditableTextArea label="Certifications" field="certifications" rows={2} />
              </InfoSection>

              {/* Job Search Preferences */}
              <InfoSection title="Job Search Preferences" icon={Briefcase}>
                <EditableRow label="Desired Roles" field="desiredJobTitles" />
                <EditableRow label="Industries" field="preferredIndustries" />
                <EditableRow label="Locations" field="preferredLocations" />
                <EditableRow label="Open to Relocation" field="openToRelocation" options={YES_NO_MAYBE_OPTIONS} />
                <EditableRow label="Willing to Travel" field="willingToTravel" />
              </InfoSection>

              {/* Compensation */}
              <InfoSection title="Compensation & Availability" icon={DollarSign}>
                <EditableRow label="Salary Range" field="desiredSalaryRange" />
                <EditableRow label="Bonus Expectations" field="bonusExpectations" />
                <EditableRow label="Availability" field="availabilityToStart" />
                <EditableTextArea label="Ideal Next Role" field="idealNextRole" rows={3} />
              </InfoSection>

              {/* Screening Questions */}
              <InfoSection title="Screening Questions" icon={Shield}>
                <EditableRow label="Authorized to Work" field="authorizedToWork" options={YES_NO_OPTIONS} />
                <EditableRow label="Requires Sponsorship" field="requiresSponsorship" options={YES_NO_OPTIONS} />
                <EditableRow label="Background Check" field="willingBackgroundCheck" options={YES_NO_OPTIONS} />
                <EditableRow label="Drug Screen" field="willingDrugScreen" options={YES_NO_OPTIONS} />
                <EditableRow label="Currently Interviewing" field="currentlyInterviewing" options={YES_NO_OPTIONS} />
              </InfoSection>
            </div>

            {/* Right Column - Status & Notes */}
            <div className="space-y-6">
              {/* Status Management */}
              <div className="bg-gray-50 p-6 rounded-lg border-2 border-[#d4a574]">
                <h3 className="text-lg font-bold text-[#0a1628] mb-4">Status Management</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(v) => handleInputChange('status', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Recruiter Notes</Label>
                    <Textarea
                      value={formData.recruiterNotes}
                      onChange={(e) => handleInputChange('recruiterNotes', e.target.value)}
                      rows={8}
                      className="mt-1"
                      placeholder="Add notes about this candidate..."
                    />
                  </div>

                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]"
                  >
                    <Save className="mr-2" size={18} />
                    {isSaving ? 'Saving...' : 'Save All Changes'}
                  </Button>
                </div>
              </div>

              {/* Metadata */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-bold text-[#0a1628] mb-4">Application Details</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold text-gray-700">Submitted:</p>
                    <p className="text-gray-900">{formatDate(applicant.submissionDate)}</p>
                  </div>
                  {applicant.lastContactDate && (
                    <div>
                      <p className="font-semibold text-gray-700">Last Contact:</p>
                      <p className="text-gray-900">{formatDate(applicant.lastContactDate)}</p>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-700">Source:</p>
                    <p className="text-gray-900">{applicant.source}</p>
                  </div>
                  {applicant.resumeFileName && (
                    <div>
                      <p className="font-semibold text-gray-700">Resume:</p>
                      <p className="text-gray-900 text-xs break-all">{applicant.resumeFileName}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="space-y-2">
                <a
                  href={`mailto:${formData.email || applicant.email}`}
                  className="block w-full"
                >
                  <Button variant="outline" className="w-full">
                    <Mail className="mr-2" size={18} />
                    Email Candidate
                  </Button>
                </a>
                <a
                  href={`tel:${formData.phoneNumber || applicant.phoneNumber}`}
                  className="block w-full"
                >
                  <Button variant="outline" className="w-full">
                    <Phone className="mr-2" size={18} />
                    Call Candidate
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-lg border-t">
          {isEditMode && (
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]"
            >
              <Save className="mr-2" size={18} />
              {isSaving ? 'Saving...' : 'Save All Changes'}
            </Button>
          )}
          <Button
            onClick={onClose}
            variant="outline"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApplicantDetailModal;
